/**
 * DeploymentsPageV2 — proper v2 redesign of Deployments (G114).
 *
 * Canonical inventory pattern.
 * Reuses every backend code path from v1 DeploymentsPage:
 *   - usePlatform: deployments, agents, orgs, tenants + their loaders
 *   - api.deployFromAgent / deleteDeployment / updateDeploymentStatus
 *
 * v1 stays as fallback via /deployments?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthAdministerEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, Plus, Server, Building2, Layers, Rocket, Play, Square, Trash2,
  Activity, ChevronRight,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import DeploymentsPage from './DeploymentsPage';

const ENV_OPTIONS = [
  { key: 'production',     label: 'Production' },
  { key: 'production-dr',  label: 'Production-DR' },
  { key: 'uat',            label: 'UAT' },
  { key: 'staging',        label: 'Staging' },
  { key: 'integration',    label: 'Integration' },
  { key: 'development',    label: 'Development' },
  { key: 'performance-testing', label: 'Performance Testing' },
];

const STATUS_OPTIONS = [
  { key: 'healthy',    label: 'Healthy',    tone: 'success' },
  { key: 'degraded',   label: 'Degraded',   tone: 'warning' },
  { key: 'critical',   label: 'Critical',   tone: 'danger' },
  { key: 'stopped',    label: 'Stopped',    tone: 'muted' },
  { key: 'pending',    label: 'Pending',    tone: 'cyan' },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));

function fmtRelative(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function DeploymentsPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const platform = usePlatform();
  const { session } = useAuth();
  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  const {
    deployments = [], agents = [], orgs = [], tenants = [],
    loadDeployments, loadAgents, loadOrganizations, loadTenants,
  } = platform;

  const reload = useCallback(() => {
    if (typeof loadDeployments === 'function') loadDeployments({ orgId, tenantId });
    if (typeof loadAgents === 'function') loadAgents({ orgId, tenantId });
    if (typeof loadOrganizations === 'function') loadOrganizations();
    if (typeof loadTenants === 'function') loadTenants();
  }, [loadDeployments, loadAgents, loadOrganizations, loadTenants, orgId, tenantId]);
  useEffect(() => { reload(); }, [reload]);

  const orgMap = useMemo(() => Object.fromEntries((orgs || []).map(o => [o.id, o.name || o.id])), [orgs]);
  const tenantMap = useMemo(() => Object.fromEntries((tenants || []).map(t => [t.id, t.name || t.id])), [tenants]);
  const agentMap = useMemo(() => Object.fromEntries((agents || []).map(a => [a.id, a.name || a.id])), [agents]);

  /* G200 — V2-native Deploy from Agent modal. Calls api.deployFromAgent
   * (POST /api/v1/deployments/from-agent/{agentId}) which is the same
   * backend V1 uses. Operator picks an Act-phase agent + environment +
   * deployment name; on success the deployments list refreshes. */
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployForm, setDeployForm] = useState({
    agentId: '', environment: 'production', name: '', notes: '',
  });
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);
  const actPhaseAgents = useMemo(
    () => (agents || []).filter(a => (a.phase || '').toLowerCase() === 'act'),
    [agents]
  );
  const openDeployModal = () => {
    setDeployForm({
      agentId: actPhaseAgents[0]?.id || '',
      environment: 'production',
      name: actPhaseAgents[0]?.name || '',
      notes: '',
    });
    setDeployError(null);
    setDeployOpen(true);
  };
  const submitDeploy = async () => {
    setDeployError(null);
    if (!deployForm.agentId) { setDeployError('Pick an agent in Act phase.'); return; }
    setDeploying(true);
    try {
      const res = await api.deployFromAgent(deployForm.agentId, {
        environment: deployForm.environment,
        name: deployForm.name.trim() || agentMap[deployForm.agentId] || 'Untitled deployment',
        notes: deployForm.notes.trim() || null,
        orgId, tenantId,
      });
      if (!res || res.ok === false) {
        setDeployError(res?.error || res?.data?.error || `Backend rejected deploy (HTTP ${res?.status || '?'}).`);
        return;
      }
      addToast({ tone: 'success', title: 'Deployment created', description: `${deployForm.name} → ${deployForm.environment}` });
      setDeployOpen(false);
      reload();
    } catch (e) {
      setDeployError(e?.message || String(e));
    } finally {
      setDeploying(false);
    }
  };

  /* Facets */
  const [facets, setFacets] = useState(() => ({
    org:    new Set(),
    tenant: new Set(),
    env:    new Set(),
    status: new Set(),
  }));
  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets(prev => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };
  const clearFacets = () => setFacets({ org: new Set(), tenant: new Set(), env: new Set(), status: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    ...((orgs || []).length > 1 ? [{ key: 'org', label: 'Organization', options: (orgs || []).map(o => ({ key: o.id, label: o.name || o.id })) }] : []),
    ...((tenants || []).length > 1 ? [{ key: 'tenant', label: 'Tenant', options: (tenants || []).map(t => ({ key: t.id, label: t.name || t.id })) }] : []),
    { key: 'env', label: 'Environment', options: ENV_OPTIONS },
    { key: 'status', label: 'Status', options: STATUS_OPTIONS.map(s => ({ key: s.key, label: s.label })) },
  ]), [orgs, tenants]);

  const filtered = useMemo(() => {
    return (deployments || []).filter(d => {
      if (facets.org.size > 0 && !facets.org.has(d.orgId)) return false;
      if (facets.tenant.size > 0 && !facets.tenant.has(d.tenantId)) return false;
      if (facets.env.size > 0 && !facets.env.has(d.environment || d.env)) return false;
      if (facets.status.size > 0 && !facets.status.has(d.status)) return false;
      return true;
    });
  }, [deployments, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = (deployments || []).filter(d => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            const field = og.key === 'org' ? d.orgId
                       : og.key === 'tenant' ? d.tenantId
                       : og.key === 'env' ? (d.environment || d.env)
                       : d.status;
            if (!sel.has(field)) return false;
          }
          const field = g.key === 'org' ? d.orgId
                     : g.key === 'tenant' ? d.tenantId
                     : g.key === 'env' ? (d.environment || d.env)
                     : d.status;
          return field === opt.key;
        }).length;
      }
    }
    return counts;
  }, [deployments, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS.map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
      }
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('deployments-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('deployments-density', next); } catch {}
  };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('summary');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('summary'); };
  const closeSlideout = () => setSelected(null);

  /* G369 — Deep-link handling. The Team → Assignments → Infrastructure
   * tree links into this workspace with one of:
   *   ?id=<deploymentId>        → auto-open detail slideout
   *   ?action=new               → auto-open Deploy-from-Agent modal
   *   ?orgId=<id>&tenantId=<id> → pre-select facets so the table
   *                               is already scoped to that branch.
   * Apply once when params + data are available, then strip the
   * params so a refresh doesn't keep re-triggering the side effects. */
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);
  useEffect(() => {
    if (deepLinkApplied) return;
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    const idParam = sp.get('id');
    const actionParam = sp.get('action');
    const orgIdParam = sp.get('orgId');
    const tenantIdParam = sp.get('tenantId');

    let mutated = false;
    if (orgIdParam || tenantIdParam) {
      setFacets((prev) => ({
        ...prev,
        org:    orgIdParam ? new Set([orgIdParam]) : prev.org,
        tenant: tenantIdParam ? new Set([tenantIdParam]) : prev.tenant,
      }));
      if (orgIdParam) { url.searchParams.delete('orgId'); mutated = true; }
      if (tenantIdParam) { url.searchParams.delete('tenantId'); mutated = true; }
    }
    if (idParam && (deployments || []).length > 0) {
      const target = (deployments || []).find((d) => d.id === idParam);
      if (target) {
        setSelected(target);
        setSlideoutTab('summary');
      }
      url.searchParams.delete('id');
      mutated = true;
    }
    if (actionParam === 'new') {
      setDeployOpen(true);
      url.searchParams.delete('action');
      mutated = true;
    }
    if (mutated) {
      window.history.replaceState({}, '', url.toString());
      setDeepLinkApplied(true);
    } else if (!idParam && !actionParam && !orgIdParam && !tenantIdParam) {
      setDeepLinkApplied(true);
    }
    // eslint-disable-next-line
  }, [deployments]);

  const handleStart = async (id) => {
    try { await api.updateDeploymentStatus?.(id, 'START'); addToast({ tone: 'success', title: 'Deployment started' }); reload(); }
    catch (err) { addToast({ tone: 'danger', title: 'Start failed', description: err?.message }); }
  };
  const handleStop = async (id) => {
    try { await api.updateDeploymentStatus?.(id, 'STOP'); addToast({ tone: 'info', title: 'Deployment stopped' }); reload(); }
    catch (err) { addToast({ tone: 'danger', title: 'Stop failed', description: err?.message }); }
  };
  const handleDelete = async (id) => {
    try { await api.deleteDeployment?.(id); addToast({ tone: 'info', title: 'Deployment deleted' }); closeSlideout(); reload(); }
    catch (err) { addToast({ tone: 'danger', title: 'Delete failed', description: err?.message }); }
  };

  const kpis = useMemo(() => ({
    total:    (deployments || []).length,
    healthy:  (deployments || []).filter(d => d.status === 'healthy').length,
    prod:     (deployments || []).filter(d => (d.environment || d.env) === 'production').length,
    orgs:     new Set((deployments || []).map(d => d.orgId).filter(Boolean)).size,
  }), [deployments]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Deployments</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {kpis.total} deployments · {kpis.healthy} healthy · {kpis.prod} in production · {filtered.length} match filters
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
          <Button variant="primary" size="sm" iconLeft={<Rocket size={13} />}
            onClick={openDeployModal}>
            Deploy from Agent
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total deployments" value={kpis.total} tone="cyan" />
        <MetricCard label="Healthy"          value={kpis.healthy} tone="success" />
        <MetricCard label="In production"    value={kpis.prod} tone="trust" />
        <MetricCard label="Organizations"    value={kpis.orgs} tone="muted" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Deployment filters" />

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Server size={20} />}
            title={(deployments || []).length === 0 ? 'No deployments yet' : 'No deployments match filters'}
            description={(deployments || []).length === 0
              ? 'Deploy your first Agentlet to a tenant environment.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={(deployments || []).length === 0
              ? <Button variant="primary" iconLeft={<Rocket />} onClick={openDeployModal}>Deploy from Agent</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="Deployments"
            density={density}
            columns={[
              { key: 'agent', header: 'Agent', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{agentMap[r.agentId] || r.agentName || r.agentId || '—'}</div>
                  <div className="text-[10.5px] text-v2-ink-muted font-mono-v2">{(r.agentId || '').slice(0, 8)}…</div>
                </div>
              )},
              { key: 'org', header: 'Org / Tenant', render: (r) => (
                <div className="text-[11.5px]">
                  <div className="text-v2-ink">{orgMap[r.orgId] || r.orgId || '—'}</div>
                  <div className="text-v2-ink-muted">{tenantMap[r.tenantId] || r.tenantId || '—'}</div>
                </div>
              )},
              { key: 'env', header: 'Env', render: (r) => (
                <Pill tone={(r.environment || r.env) === 'production' ? 'danger' : (r.environment || r.env) === 'staging' ? 'warning' : 'cyan'} size="sm">
                  {r.environment || r.env || '—'}
                </Pill>
              )},
              { key: 'status', header: 'Status', render: (r) => (
                <Pill tone={STATUS_TONE[r.status] || 'muted'} size="sm" dot>{r.status || 'unknown'}</Pill>
              )},
              { key: 'version', header: 'Version', render: (r) => (
                <span className="font-mono-v2 text-[11px]">{r.version || r.agentVersion || '—'}</span>
              )},
              { key: 'lastActivity', header: 'Last activity', align: 'right', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{fmtRelative(r.lastActivityAt || r.updatedAt || r.createdAt)}</span>
              )},
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  {r.status === 'stopped' || r.status === 'pending' ? (
                    <Button variant="ghost" size="sm" className="!text-v2-success hover:!bg-v2-success-soft"
                      onClick={() => handleStart(r.id)} title="Start">
                      <Play size={13} />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => handleStop(r.id)} title="Stop">
                      <Square size={13} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                    onClick={() => handleDelete(r.id)} title="Delete">
                    <Trash2 size={13} />
                  </Button>
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="deployments"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `Deployment ${selected.id} detail` : 'Deployment detail'}
        width={520}
        tabs={[
          { id: 'summary', label: 'Summary' },
          { id: 'agents',  label: 'Deployed agents' },
          { id: 'usage',   label: 'Resource usage' },
          { id: 'audit',   label: 'Audit' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={(selected.environment || selected.env) === 'production' ? 'danger' : 'cyan'} size="sm">
                {selected.environment || selected.env}
              </Pill>
              <Pill tone={STATUS_TONE[selected.status] || 'muted'} size="sm" dot>{selected.status}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">
              {agentMap[selected.agentId] || selected.agentName || selected.agentId}
            </h2>
            <p className="text-[12px] text-v2-ink-secondary">
              {orgMap[selected.orgId] || selected.orgId} · {tenantMap[selected.tenantId] || selected.tenantId}
            </p>
            <div className="flex items-center gap-2 mt-3">
              {selected.status === 'stopped' || selected.status === 'pending' ? (
                <Button variant="primary" size="sm" iconLeft={<Play size={13} />} onClick={() => handleStart(selected.id)}>Start</Button>
              ) : (
                <Button variant="secondary" size="sm" iconLeft={<Square size={13} />} onClick={() => handleStop(selected.id)}>Stop</Button>
              )}
              <Button variant="ghost" size="sm" className="!text-v2-danger" iconLeft={<Trash2 size={13} />} onClick={() => handleDelete(selected.id)}>Delete</Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'summary' && (
          <Card padding="sm">
            <Card.Header title="Deployment summary" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Last activity</dt><dd className="text-v2-ink mt-0.5">{selected.lastActivityAt ? new Date(selected.lastActivityAt).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Version</dt><dd className="text-v2-ink mt-0.5 font-mono-v2">{selected.version || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Gateway URL</dt><dd className="text-v2-ink mt-0.5 font-mono-v2 text-[11px]">{selected.gatewayUrl || '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'agents' && (
          <Card padding="sm">
            <Card.Header title="Deployed agents" />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink leading-relaxed">
                <strong>{agentMap[selected.agentId] || selected.agentName}</strong> is deployed to this instance.
              </p>
              <Button variant="ghost" size="sm" className="mt-2"
                onClick={() => navigate(`/agents/${selected.agentId}`)}>
                Open agent detail →
              </Button>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'usage' && (
          <Card padding="sm">
            <Card.Header title="Resource usage" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">CPU</dt><dd className="text-v2-ink mt-0.5">{selected.cpuPct != null ? `${selected.cpuPct}%` : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Memory</dt><dd className="text-v2-ink mt-0.5">{selected.memPct != null ? `${selected.memPct}%` : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Requests/24h</dt><dd className="text-v2-ink mt-0.5">{selected.requests24h != null ? selected.requests24h.toLocaleString() : '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">P95 latency</dt><dd className="text-v2-ink mt-0.5">{selected.p95LatencyMs ? `${selected.p95LatencyMs} ms` : '—'}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'audit' && (
          <Card padding="sm">
            <Card.Header title="Audit trail" actions={
              /* G436 — Full-audit deep-link. The prop's expression
                 container must hold exactly ONE JSX child; an
                 adjacent comment node would make this two siblings
                 and break parsing across the whole bundle. */
              <Button variant="ghost" size="sm" onClick={() => navigate(`/workflow-audit?deploymentId=${selected.id}`)}>Full audit →</Button>
            } />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">Recent deployment activity will appear here once the v2 audit drilldown is wired.</p>
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G200 — Deploy from Agent modal. */}
      <Modal
        isOpen={deployOpen}
        onClose={() => setDeployOpen(false)}
        title="Deploy from Agent"
        dismissOnBackdrop={!deploying}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDeployOpen(false)} disabled={deploying}>Cancel</Button>
            <Button variant="primary" size="sm" iconLeft={<Rocket size={13} />}
              onClick={submitDeploy}
              disabled={deploying || !deployForm.agentId}>
              {deploying ? 'Deploying…' : 'Deploy'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3.5">
          {actPhaseAgents.length === 0 ? (
            <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft text-v2-warning-strong p-3 text-[12.5px]">
              No agents are currently in <strong>Act</strong> phase. An agent must complete Build → Decide → Act before it can be deployed. Open an agent and complete its phase gates first.
            </div>
          ) : (
            <>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Agent</span>
                <select
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={deployForm.agentId}
                  onChange={(e) => {
                    const newId = e.target.value;
                    const a = actPhaseAgents.find(x => x.id === newId);
                    setDeployForm(f => ({
                      ...f,
                      agentId: newId,
                      name: f.name && f.name !== agentMap[f.agentId] ? f.name : (a?.name || ''),
                    }));
                  }}
                  disabled={deploying}
                >
                  {actPhaseAgents.map(a => (
                    <option key={a.id} value={a.id}>{a.name || a.id} — {a.archetype || 'Let'}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Environment</span>
                <select
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={deployForm.environment}
                  onChange={(e) => setDeployForm(f => ({ ...f, environment: e.target.value }))}
                  disabled={deploying}
                >
                  {ENV_OPTIONS.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Deployment name</span>
                <input
                  type="text"
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  value={deployForm.name}
                  onChange={(e) => setDeployForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={agentMap[deployForm.agentId] || 'Untitled deployment'}
                  disabled={deploying}
                />
              </label>

              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Notes (optional)</span>
                <textarea
                  className="mt-1 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px] text-v2-ink"
                  rows={3}
                  value={deployForm.notes}
                  onChange={(e) => setDeployForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Why is this being deployed? (optional)"
                  disabled={deploying}
                />
              </label>

              {deployError && (
                <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
                  {deployError}
                </div>
              )}
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default function ResolvedDeploymentsPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy || !isRebirthAdministerEnabled()) return <DeploymentsPage {...props} />;
  return <DeploymentsPageV2Inner {...props} />;
}

export { DeploymentsPageV2Inner };
